import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ApiError,
  errorMessage,
  useDeleteMapping,
  useEffective,
  useGlobalSettings,
  useGuilds,
  useMapping,
  usePatchMapping,
  useTestMapping,
  type SettingOriginName,
  type SettingValue,
} from "@/api";
import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { SettingsForm } from "@/components/SettingsForm";
import { mappingStatus } from "@/lib/status";
import { useDocumentTitle } from "@/components/useDocumentTitle";
import {
  describeFallback,
  formatValue,
  globalOverrides,
  humanize,
  resolve,
  touchesKey,
} from "@/lib/settings";
import {
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Mono,
  Skeleton,
  StatusDot,
  Switch,
  useToast,
} from "@/ui";
import "./pages.css";

const ORIGIN_WORD: Record<SettingOriginName, string> = {
  default: "default",
  global: "global",
  guild: "guild",
  mapping: "this mapping",
};

export function MappingDetailPage() {
  const id = Number(useParams().id);
  const navigate = useNavigate();
  const toast = useToast();
  const mappingQuery = useMapping(id);
  const effective = useEffective(id);
  const global = useGlobalSettings();
  const guilds = useGuilds();
  const patch = usePatchMapping();
  const del = useDeleteMapping();
  const test = useTestMapping();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const mapping = mappingQuery.data;
  useDocumentTitle(mapping ? `#${mapping.source_channel.name}` : `Mapping ${id}`);

  if (!Number.isInteger(id)) return <NotFoundCard />;
  if (mappingQuery.error instanceof ApiError && mappingQuery.error.status === 404) {
    return <NotFoundCard />;
  }

  const spec = global.data?.spec;
  const eff = effective.data;

  // What a reset would fall back to: resolve without this mapping's override.
  const guildSettings = guilds.data?.items.find(
    (g) => g.id === mapping?.source_channel.guild_id,
  )?.settings;
  const fallback = spec ? resolve(spec, globalOverrides(global.data), guildSettings, {}) : null;

  const save = async (body: Parameters<typeof patch.mutateAsync>[0]["patch"], done: string) => {
    try {
      await patch.mutateAsync({ id, patch: body });
      toast.show({ tone: "success", title: done });
    } catch (e) {
      toast.show({ tone: "danger", title: "Could not save", message: errorMessage(e) });
    }
  };

  const onSet = (key: string, value: SettingValue) =>
    save({ settings_set: { [key]: value } }, `${humanize(key)}: ${formatValue(value)}`);
  const onReset = (key: string) =>
    save({ settings_reset: [key] }, `${humanize(key)} reset to inherited`);

  const runTest = () =>
    test.mutate(id, {
      onSuccess: () =>
        toast.show({ tone: "info", title: "Test message queued", message: "See Jobs for the result." }),
      onError: (e) => toast.show({ tone: "danger", title: "Test failed", message: errorMessage(e) }),
    });

  const st = mapping ? mappingStatus(mapping) : null;

  return (
    <div className="page">
      <PageHeader
        crumb={
          <Link className="page-crumb" to="/mappings">
            ← Mappings
          </Link>
        }
        title={
          mapping ? `#${mapping.source_channel.name} → #${mapping.target_channel.name}` : "Mapping"
        }
        description={mapping ? mapping.source_channel.guild_name : undefined}
        actions={
          <>
            <Button variant="secondary" onClick={runTest} loading={test.isPending} disabled={!mapping}>
              Send test message
            </Button>
            <Button variant="danger" onClick={() => setConfirmDelete(true)} disabled={!mapping}>
              Delete
            </Button>
          </>
        }
      />
      <ErrorBanner error={mappingQuery.error ?? effective.error ?? global.error} onRetry={() => {
        mappingQuery.refetch();
        effective.refetch();
        global.refetch();
      }} />

      <Card>
        {mapping && st ? (
          <div className="account-row">
            <dl className="kv">
              <dt>Status</dt>
              <dd>
                <StatusDot status={st.status} label={st.label} />
              </dd>
              <dt>Source channel</dt>
              <dd>
                <Mono value={mapping.source_channel.id} />
              </dd>
              <dt>Target channel</dt>
              <dd>
                <Mono value={mapping.target_channel.id} />
              </dd>
            </dl>
            <Switch
              label="Enabled"
              checked={mapping.enabled}
              disabled={patch.isPending}
              onChange={(next) =>
                save({ enabled: next }, next ? "Mapping enabled" : "Mapping disabled")
              }
            />
          </div>
        ) : (
          <Skeleton lines={3} />
        )}
      </Card>

      <Card title="Effective settings">
        {spec && eff ? (
          <SettingsForm
            spec={spec}
            level="mapping"
            row={(key) => {
              const editable = spec[key].levels.includes("mapping");
              const levels = spec[key].levels;
              return {
                value: eff.effective[key],
                origin: eff.origin[key],
                editable,
                note: editable
                  ? undefined
                  : `Set in ${levels.includes("guild") ? "server or global" : "global"} settings`,
              };
            }}
            inheritedLabel={(key) =>
              fallback
                ? describeFallback(fallback.effective[key], ORIGIN_WORD[fallback.origin[key]])
                : undefined
            }
            onSet={onSet}
            onReset={onReset}
            saving={(k) =>
              patch.isPending &&
              touchesKey(k, patch.variables?.patch.settings_set, patch.variables?.patch.settings_reset)
            }
          />
        ) : (
          <Skeleton lines={10} />
        )}
      </Card>

      <ConfirmDialog
        open={confirmDelete}
        destructive
        title="Delete 1 mapping"
        message="Messages stop relaying for this channel. The target channel stays in your server."
        confirmLabel="Delete mapping"
        onCancel={() => setConfirmDelete(false)}
        onConfirm={async () => {
          try {
            await del.mutateAsync(id);
            toast.show({ tone: "success", title: "Mapping deleted" });
            navigate("/mappings", { replace: true });
          } catch (e) {
            toast.show({ tone: "danger", title: "Could not delete", message: errorMessage(e) });
            setConfirmDelete(false);
          }
        }}
      />
    </div>
  );
}

function NotFoundCard() {
  return (
    <div className="page">
      <Card>
        <EmptyState
          title="Mapping not found"
          description="It may have been deleted."
          action={
            <Link className="link" to="/mappings">
              Back to mappings
            </Link>
          }
        />
      </Card>
    </div>
  );
}
